import React, { useEffect, useState } from 'react';
import { getSentimentTimingConfig, saveSentimentTimingConfig } from '../../lib/api/kb-api';

// 기본값 (백엔드에 아무것도 없을 때). /compare 매수심리 색상 기준.
const DEFAULT_CONFIG = [
  { category: 'BUY_TIMING', label: '매수타이밍', color: '#FFC7CE', sellerOp: '>=', sellerValue: 80, buyerOp: '<=', buyerValue: 1.5, indexOp: '<=', indexValue: 20, sortOrder: 1 },
  { category: 'BUY_WAIT', label: '매수대기', color: '#FFE181', sellerOp: '>=', sellerValue: 70, buyerOp: '<=', buyerValue: 3, indexOp: '<=', indexValue: 30, sortOrder: 2 },
  { category: 'SELL_TIMING', label: '매도타이밍', color: '#C9C9FF', sellerOp: '<=', sellerValue: 20, buyerOp: '>=', buyerValue: 20, indexOp: '>=', indexValue: 100, sortOrder: 3 },
  { category: 'SELL_WAIT', label: '매도대기', color: '#A7FFCF', sellerOp: '<=', sellerValue: 30, buyerOp: '>=', buyerValue: 10, indexOp: '>=', indexValue: 80, sortOrder: 4 },
];

const OP_OPTIONS = ['>=', '<=', '>', '<'];

const normalize = (list) => (Array.isArray(list) ? list : [])
  .map((c) => ({
    category: c.category,
    label: c.label ?? '',
    color: c.color ?? '#ffffff',
    sellerOp: c.sellerOp ?? '>=',
    sellerValue: Number(c.sellerValue ?? 0),
    buyerOp: c.buyerOp ?? '<=',
    buyerValue: Number(c.buyerValue ?? 0),
    indexOp: c.indexOp ?? '<=',
    indexValue: Number(c.indexValue ?? 0),
    sortOrder: Number(c.sortOrder ?? 0),
  }))
  .sort((a, b) => a.sortOrder - b.sortOrder);

function SentimentTimingManagement() {
  const [rows, setRows] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const load = async () => {
    setLoading(true);
    setMessage('');
    try {
      const res = await getSentimentTimingConfig();
      const list = Array.isArray(res) ? res : (res?.data ?? res?.items ?? []);
      setRows(list && list.length > 0 ? normalize(list) : DEFAULT_CONFIG);
    } catch (e) {
      setRows(DEFAULT_CONFIG);
      setMessage('기존 설정을 불러오지 못해 기본값을 표시합니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateRow = (idx, field, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const payload = rows.map((r) => ({
        category: r.category,
        label: String(r.label || '').trim(),
        color: String(r.color || '#ffffff').trim(),
        sellerOp: r.sellerOp,
        sellerValue: Number(r.sellerValue),
        buyerOp: r.buyerOp,
        buyerValue: Number(r.buyerValue),
        indexOp: r.indexOp,
        indexValue: Number(r.indexValue),
        sortOrder: Number(r.sortOrder),
      }));
      const res = await saveSentimentTimingConfig(payload);
      if (res?.success === false) throw new Error('저장 실패');
      setMessage('저장되었습니다.');
    } catch (e) {
      setMessage('저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const cell = { padding: '8px 10px', borderBottom: '1px solid #eef2f7', textAlign: 'center', fontSize: 13 };
  const th = { ...cell, background: '#f8fafc', fontWeight: 700, color: '#334155', borderBottom: '1px solid #e2e8f0' };
  const numInput = { width: 70, height: 30, border: '1px solid #cbd5e1', borderRadius: 6, padding: '0 6px', textAlign: 'right', fontSize: 13 };
  const opSelect = { height: 30, border: '1px solid #cbd5e1', borderRadius: 6, padding: '0 4px', fontSize: 13 };

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>매수심리 타이밍 색상 기준</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={() => setRows(DEFAULT_CONFIG)} style={{ height: 34, padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff', color: '#475569', fontWeight: 700, cursor: 'pointer' }}>기본값</button>
          <button type="button" onClick={load} disabled={loading} style={{ height: 34, padding: '0 12px', border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff', color: '#475569', fontWeight: 700, cursor: 'pointer' }}>새로고침</button>
          <button type="button" onClick={handleSave} disabled={saving} style={{ height: 34, padding: '0 16px', border: 'none', borderRadius: 8, background: '#2563eb', color: '#fff', fontWeight: 800, cursor: 'pointer' }}>{saving ? '저장 중...' : '저장'}</button>
        </div>
      </div>

      <p style={{ margin: '0 0 10px', fontSize: 13, color: '#64748b' }}>
        매도세=매도자많음 · 매수세=매수자많음 · 매수우위=매수우위지수. 순위(작을수록 먼저) 순으로 판정해 처음 매칭되는 색을 적용합니다.
      </p>

      {message && <div style={{ marginBottom: 10, fontSize: 13, color: '#0f766e' }}>{message}</div>}

      <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 780 }}>
          <thead>
            <tr>
              <th style={th}>순위</th>
              <th style={th}>구분</th>
              <th style={th}>색상</th>
              <th style={th}>매도세</th>
              <th style={th}>매수세</th>
              <th style={th}>매수우위</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={r.category}>
                <td style={cell}>
                  <input type="number" value={r.sortOrder} onChange={(e) => updateRow(idx, 'sortOrder', e.target.value)} style={{ ...numInput, width: 54, textAlign: 'center' }} />
                </td>
                <td style={cell}>
                  <input type="text" value={r.label} onChange={(e) => updateRow(idx, 'label', e.target.value)} style={{ width: 90, height: 30, border: '1px solid #cbd5e1', borderRadius: 6, padding: '0 8px', fontSize: 13, fontWeight: 700 }} />
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{r.category}</div>
                </td>
                <td style={cell}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <input type="color" value={/^#([0-9a-fA-F]{6})$/.test(r.color) ? r.color : '#ffffff'} onChange={(e) => updateRow(idx, 'color', e.target.value)} style={{ width: 34, height: 30, border: '1px solid #cbd5e1', borderRadius: 6, padding: 0, cursor: 'pointer' }} />
                    <input type="text" value={r.color} onChange={(e) => updateRow(idx, 'color', e.target.value)} style={{ width: 84, height: 30, border: '1px solid #cbd5e1', borderRadius: 6, padding: '0 6px', fontSize: 12 }} />
                  </span>
                </td>
                <td style={cell}>
                  <select value={r.sellerOp} onChange={(e) => updateRow(idx, 'sellerOp', e.target.value)} style={opSelect}>
                    {OP_OPTIONS.map((op) => <option key={op} value={op}>{op}</option>)}
                  </select>{' '}
                  <input type="number" step="0.1" value={r.sellerValue} onChange={(e) => updateRow(idx, 'sellerValue', e.target.value)} style={numInput} />
                </td>
                <td style={cell}>
                  <select value={r.buyerOp} onChange={(e) => updateRow(idx, 'buyerOp', e.target.value)} style={opSelect}>
                    {OP_OPTIONS.map((op) => <option key={op} value={op}>{op}</option>)}
                  </select>{' '}
                  <input type="number" step="0.1" value={r.buyerValue} onChange={(e) => updateRow(idx, 'buyerValue', e.target.value)} style={numInput} />
                </td>
                <td style={cell}>
                  <select value={r.indexOp} onChange={(e) => updateRow(idx, 'indexOp', e.target.value)} style={opSelect}>
                    {OP_OPTIONS.map((op) => <option key={op} value={op}>{op}</option>)}
                  </select>{' '}
                  <input type="number" step="0.1" value={r.indexValue} onChange={(e) => updateRow(idx, 'indexValue', e.target.value)} style={numInput} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SentimentTimingManagement;
